import React, { useRef, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import HeroSlide from './HeroSlide';

const TRANSITION_MS = 600;
const WHEEL_THRESHOLD = 20;
const TRANSITION = `transform ${TRANSITION_MS}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`;

/**
 * Which slides may load their video.
 *
 * Positions run 0 = clone of the last card, 1..slideCount = the real cards,
 * slideCount + 1 = clone of the first card. Liveness is decided by position
 * rather than card index because the clones share a card index with a real
 * slide, which would make "the current one" ambiguous.
 *
 * Every slide still mounts; without this gate each one downloads its own video
 * on page load, which is around 40MB on the current homepage.
 */
export const isSlideLive = (position, visualIndex, slideCount, hasNavigated) => {
  /**
   * A single card still renders both clones so the transform maths stays put,
   * but they are never scrolled to, so only the real slide needs its video.
   */
  if (slideCount <= 1) {
    return position === 1;
  }

  if (Math.abs(position - visualIndex) <= 1) {
    return true;
  }

  /**
   * Keep the first paint to the three slides above. The wrap partners below only
   * matter once someone starts navigating, and the first move cannot itself land
   * a snap before this flips.
   */
  if (!hasNavigated) {
    return false;
  }

  /**
   * The wrap animates onto a clone and then snaps, with transitions disabled, to
   * the real slide showing the same card. That slide has to be warm before the
   * snap or the wrap flashes the backdrop.
   */
  if (visualIndex >= slideCount && position === 1) {
    return true;
  }

  return visualIndex <= 1 && position === slideCount;
};

const HeroCarousel = ({
                        cards,
                        currentIndex,
                        onKeyDown,
                        onCtaClick,
                        onIndexChange
                      }) => {
  const carouselRef = useRef(null);
  const isAnimating = useRef(false);
  const pendingSnapIndex = useRef(null);
  const transitionFallbackTimeout = useRef(null);
  const restoreTransitionFrame = useRef(null);
  const hasNavigated = useRef(false);

  /**
   * Once a slide has loaded its video, keep it mounted even when it drops out of
   * the live window. Unmounting would throw away the buffered data, and the CDN
   * serves these files `no-store`, so coming back would download the whole thing
   * again.
   */
  const loadedPositions = useRef(new Set());

  const slideCount = cards?.length || 0;

  const safeCurrentIndex = slideCount > 0
    ? Math.min(Math.max(currentIndex, 0), slideCount - 1)
    : 0;

  // visualIndex is currentIndex + 1, mostly
  const [visualIndex, setVisualIndex] = useState(() => (
    slideCount > 0 ? safeCurrentIndex + 1 : 0
  ));

  const [isTransitionEnabled, setIsTransitionEnabled] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);

  useEffect(() => {
    if (carouselRef.current) {
      carouselRef.current.focus();
    }
  }, []);

  const clearTransitionFallback = useCallback(() => {
    if (transitionFallbackTimeout.current) {
      clearTimeout(transitionFallbackTimeout.current);
      transitionFallbackTimeout.current = null;
    }
  }, []);

  const clearRestoreTransitionFrame = useCallback(() => {
    if (restoreTransitionFrame.current) {
      cancelAnimationFrame(restoreTransitionFrame.current);
      restoreTransitionFrame.current = null;
    }
  }, []);

  const finishTransition = useCallback(() => {
    if (!isAnimating.current) {
      return;
    }

    clearTransitionFallback();

    const snapIndex = pendingSnapIndex.current;

    if (snapIndex !== null) {
      pendingSnapIndex.current = null;

      /**
       * Snap from clone to matching real slide with no transition.
       *
       * Example, scrolling down from last to first:
       * animate to visualIndex slideCount + 1, the first-slide clone
       * then snap to visualIndex 1, the real first slide
       */
      setIsTransitionEnabled(false);
      setVisualIndex(snapIndex);

      /**
       * Wait for the non-animated snap to be committed before re-enabling
       * transitions. Two animation frames avoids the browser combining the
       * snap and the transition re-enable into one paint.
       */
      restoreTransitionFrame.current = requestAnimationFrame(() => {
        restoreTransitionFrame.current = requestAnimationFrame(() => {
          setIsTransitionEnabled(true);
          setIsScrolling(false);
          isAnimating.current = false;
          restoreTransitionFrame.current = null;
        });
      });

      return;
    }

    setIsScrolling(false);
    isAnimating.current = false;
  }, [clearTransitionFallback]);

  const startTransitionTo = useCallback((targetVisualIndex, snapIndex = null) => {
    clearTransitionFallback();
    clearRestoreTransitionFrame();

    pendingSnapIndex.current = snapIndex;
    isAnimating.current = true;

    setIsScrolling(true);
    setIsTransitionEnabled(true);
    setVisualIndex(targetVisualIndex);

    /**
     * Fallback in case transitionend does not fire, for example if the browser
     * skips the transition under load.
     */
    transitionFallbackTimeout.current = setTimeout(
      finishTransition,
      TRANSITION_MS + 80
    );
  }, [
    clearRestoreTransitionFrame,
    clearTransitionFallback,
    finishTransition
  ]);

  const notifyIndexChange = useCallback((nextIndex, direction, event) => {
    if (onIndexChange) {
      onIndexChange(nextIndex, direction, event);
      return;
    }

    /**
     * Backward-compatible fallback for parents that currently update
     * currentIndex inside onKeyDown.
     *
     * Prefer onIndexChange going forward.
     */
    if (onKeyDown) {
      onKeyDown(direction === 'next' ? 'ArrowDown' : 'ArrowUp', event);
    }
  }, [onIndexChange, onKeyDown]);

  const move = useCallback((direction, event) => {
    if (slideCount <= 1 || isAnimating.current) {
      return false;
    }

    const isNext = direction === 'next';

    /**
     * Set before startTransitionTo: that call updates visualIndex, so the render
     * which first shows the new position also sees this flag.
     */
    hasNavigated.current = true;

    let nextIndex;
    let targetVisualIndex;
    let snapIndex = null;

    if (isNext) {
      if (safeCurrentIndex === slideCount - 1) {
        /**
         * Last real slide -> trailing first-slide clone.
         * After the animation, snap invisibly to the real first slide.
         */
        nextIndex = 0;
        targetVisualIndex = slideCount + 1;
        snapIndex = 1;
      } else {
        nextIndex = safeCurrentIndex + 1;
        targetVisualIndex = nextIndex + 1;
      }
    } else if (safeCurrentIndex === 0) {
      /**
       * First real slide -> leading last-slide clone.
       * After the animation, snap invisibly to the real last slide.
       */
      nextIndex = slideCount - 1;
      targetVisualIndex = 0;
      snapIndex = slideCount;
    } else {
      nextIndex = safeCurrentIndex - 1;
      targetVisualIndex = nextIndex + 1;
    }

    startTransitionTo(targetVisualIndex, snapIndex);
    notifyIndexChange(nextIndex, direction, event);

    return true;
  }, [
    notifyIndexChange,
    safeCurrentIndex,
    slideCount,
    startTransitionTo
  ]);

  useEffect(() => {
    if (slideCount === 0 || isAnimating.current) {
      return;
    }

    setIsTransitionEnabled(true);
    setVisualIndex(safeCurrentIndex + 1);
  }, [safeCurrentIndex, slideCount]);

  const handleWheel = useCallback((e) => {
    if (Math.abs(e.deltaY) < WHEEL_THRESHOLD) {
      return;
    }

    e.preventDefault();

    if (isAnimating.current || slideCount <= 1) {
      return;
    }

    move(e.deltaY > 0 ? 'next' : 'prev', e);
  }, [move, slideCount]);

  useEffect(() => {
    const el = carouselRef.current;

    if (!el) {
      return undefined;
    }

    el.addEventListener('wheel', handleWheel, { passive: false });

    let touchStartY = 0;
    let touchStartX = 0;
    const MIN_SWIPE_DISTANCE = 50;

    const handleTouchStart = (e) => {
      touchStartY = e.touches[0].clientY;
      touchStartX = e.touches[0].clientX;
    };

    const handleTouchMove = (e) => {
      if (slideCount <= 1 || isAnimating.current) {
        return;
      }

      const touchY = e.touches[0].clientY;
      const touchX = e.touches[0].clientX;
      const deltaY = touchStartY - touchY;
      const deltaX = touchStartX - touchX;

      if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > MIN_SWIPE_DISTANCE) {
        e.preventDefault();
        touchStartY = touchY;
        touchStartX = touchX;
        move(deltaY > 0 ? 'next' : 'prev', e);
      }
    };

    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      el.removeEventListener('wheel', handleWheel);
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
    };
  }, [handleWheel, move, slideCount]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      move('next', e);
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      move('prev', e);
      return;
    }

    if (onKeyDown) {
      onKeyDown(e.key, e);
    }
  }, [move, onKeyDown]);

  const handleTransitionEnd = useCallback((e) => {
    if (e.target !== e.currentTarget || e.propertyName !== 'transform') {
      return;
    }

    finishTransition();
  }, [finishTransition]);

  useEffect(() => () => {
    clearTransitionFallback();
    clearRestoreTransitionFrame();
  }, [clearRestoreTransitionFrame, clearTransitionFallback]);

  if (!cards || slideCount === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-900">
        <div className="text-white">No cards available</div>
      </div>
    );
  }

  const renderSlide = (card, realIndex, key, position, isClone = false) => {
    if (isSlideLive(position, visualIndex, slideCount, hasNavigated.current)) {
      loadedPositions.current.add(position);
    }

    return (
      <div
        key={key}
        aria-hidden={isClone ? true : undefined}
        style={{
          flex: '0 0 100%',
          width: '100%',
          height: '100%'
        }}
      >
        <HeroSlide
          card={card}
          index={realIndex}
          totalSlides={slideCount}
          shouldLoad={loadedPositions.current.has(position)}
          isActive={position === visualIndex}
          isSettled={!isScrolling}
          onCtaClick={onCtaClick}
        />
      </div>
    );
  };

  /* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */
  return (
    <div
      ref={carouselRef}
      role="region"
      aria-roledescription="carousel"
      aria-label="Featured promotions"
      style={{
        width: '100%',
        height: '100vh',
        position: 'relative',
        overflow: 'hidden',
        cursor: isScrolling ? 'grabbing' : 'grab'
      }}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '100%',
          transition: isTransitionEnabled ? TRANSITION : 'none',
          transform: `translateY(-${visualIndex * 100}%)`,
          willChange: 'transform'
        }}
        onTransitionEnd={handleTransitionEnd}
      >
        {renderSlide(
          cards[slideCount - 1],
          slideCount - 1,
          `clone-before-${cards[slideCount - 1].id || slideCount - 1}`,
          0,
          true
        )}

        {cards.map((card, index) => renderSlide(
          card,
          index,
          `slide-${card.id || index}`,
          index + 1
        ))}

        {renderSlide(
          cards[0],
          0,
          `clone-after-${cards[0].id || 0}`,
          slideCount + 1,
          true
        )}
      </div>
    </div>
  );
};

HeroCarousel.propTypes = {
  cards: PropTypes.arrayOf(PropTypes.object).isRequired,
  currentIndex: PropTypes.number.isRequired,
  onKeyDown: PropTypes.func,
  onCtaClick: PropTypes.func,
  onIndexChange: PropTypes.func
};

HeroCarousel.defaultProps = {
  onKeyDown: null,
  onCtaClick: null,
  onIndexChange: null
};

export default HeroCarousel;
